/**
 * 统一状态标签组件
 *
 * @description 提供统一的状态标签样式，确保所有状态显示的一致性
 * @version 1.1.0
 * @author AI Assistant
 * @date 2025-01-21
 *
 * @example
 * <UnifiedStatusTag type="questionnaire" status="approved" />
 * <UnifiedStatusTag type="riskLevel" status="low" />
 *
 * @see docs/design/UI_CONSISTENCY_FRAMEWORK.md
 */

import React from 'react';
import { Tag, TagProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { EMERALD_THEME } from '../../config/designTokens';
import {
  EditOutlined,
  SendOutlined,
  ClockCircleOutlined,
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SafetyCertificateOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  DeleteOutlined,
  CrownOutlined,
  BankOutlined,
  BuildOutlined,
  ToolOutlined,
  StopOutlined,
  FileTextOutlined,
  DollarOutlined,
  FormOutlined,
  FileDoneOutlined,
} from '@ant-design/icons';

// 状态配置接口
interface StatusConfig {
  color: string;
  background: string;
  borderColor: string;
  text: string;
  icon?: React.ReactNode;
}

// 统一状态配置 - 使用设计系统颜色
const UNIFIED_STATUS_CONFIG = {
  // 问卷状态
  questionnaire: {
    draft: {
      color: EMERALD_THEME.colors.status.draft,
      background: '#f5f5f5',
      borderColor: '#d9d9d9',
      text: 'Draft',
      icon: <EditOutlined />,
    },
    submitted: {
      color: EMERALD_THEME.colors.status.submitted,
      background: '#e6f7ff',
      borderColor: '#91d5ff',
      text: 'Submitted',
      icon: <SendOutlined />,
    },
    under_review: {
      color: EMERALD_THEME.colors.status.under_review,
      background: '#f9f0ff',
      borderColor: '#d3adf7',
      text: 'In Review',
      icon: <ClockCircleOutlined />,
    },
    approved: {
      color: EMERALD_THEME.colors.status.approved,
      background: EMERALD_THEME.colors.primary[50],
      borderColor: EMERALD_THEME.colors.primary[200],
      text: 'Approved',
      icon: <CheckCircleOutlined />,
    },
    rejected: {
      color: EMERALD_THEME.colors.status.rejected,
      background: '#fff2f0',
      borderColor: '#ffccc7',
      text: 'Rejected',
      icon: <CloseCircleOutlined />,
    },
    need_supplement: {
      color: EMERALD_THEME.colors.status.supplement,
      background: '#fff7e6',
      borderColor: '#ffd591',
      text: 'Needs Supplement',
      icon: <ExclamationCircleOutlined />,
    },
    pending: {
      color: EMERALD_THEME.colors.status.pending,
      background: '#f5f5f5',
      borderColor: '#d9d9d9',
      text: 'Pending Review',
      icon: <ClockCircleOutlined />,
    },
  },

  // 风险等级
  riskLevel: {
    low: {
      color: EMERALD_THEME.colors.status.completed,
      background: EMERALD_THEME.colors.primary[50],
      borderColor: EMERALD_THEME.colors.primary[200],
      text: 'Low Risk',
      icon: <SafetyCertificateOutlined />,
    },
    medium: {
      color: EMERALD_THEME.colors.status.in_progress,
      background: '#fff7e6',
      borderColor: '#ffd666',
      text: 'Medium Risk',
      icon: <ExclamationCircleOutlined />,
    },
    high: {
      color: EMERALD_THEME.colors.status.rejected,
      background: '#fff2f0',
      borderColor: '#ffccc7',
      text: 'High Risk',
      icon: <WarningOutlined />,
    },
  },

  // 渠道状态
  channel: {
    active: {
      color: EMERALD_THEME.colors.status.completed,
      background: EMERALD_THEME.colors.primary[50],
      borderColor: EMERALD_THEME.colors.primary[200],
      text: 'Active',
      icon: <PlayCircleOutlined />,
    },
    inactive: {
      color: EMERALD_THEME.colors.neutral[500],
      background: EMERALD_THEME.colors.neutral[100],
      borderColor: EMERALD_THEME.colors.neutral[300],
      text: 'Inactive',
      icon: <PauseCircleOutlined />,
    },
    suspended: {
      color: EMERALD_THEME.colors.status.in_progress,
      background: '#fff7e6',
      borderColor: '#ffd666',
      text: 'Suspended',
      icon: <WarningOutlined />,
    },
  },

  // 项目状态
  project: {
    active: {
      color: EMERALD_THEME.colors.primary[500],
      background: EMERALD_THEME.colors.primary[50],
      borderColor: EMERALD_THEME.colors.primary[200],
      text: 'In Progress',
      icon: <PlayCircleOutlined />,
    },
    completed: {
      color: EMERALD_THEME.colors.status.completed,
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'Completed',
      icon: <CheckCircleOutlined />,
    },
    pending: {
      color: EMERALD_THEME.colors.status.in_progress,
      background: '#fff7e6',
      borderColor: '#ffd666',
      text: 'Pending',
      icon: <ClockCircleOutlined />,
    },
    suspended: {
      color: EMERALD_THEME.colors.neutral[500],
      background: EMERALD_THEME.colors.neutral[100],
      borderColor: EMERALD_THEME.colors.neutral[300],
      text: 'Suspended',
      icon: <PauseCircleOutlined />,
    },
  },

  // 审核决策
  decision: {
    approved: {
      color: '#52c41a',
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'Approved',
      icon: <CheckCircleOutlined />,
    },
    rejected: {
      color: '#ff4d4f',
      background: '#fff2f0',
      borderColor: '#ffadd2',
      text: 'Rejected',
      icon: <CloseCircleOutlined />,
    },
    pending: {
      color: '#faad14',
      background: '#fff7e6',
      borderColor: '#ffd666',
      text: 'Pending Review',
      icon: <ClockCircleOutlined />,
    },
  },

  // 用户状态
  userStatus: {
    active: {
      color: '#52c41a',
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'Active',
      icon: <CheckCircleOutlined />,
    },
    pending: {
      color: '#fa8c16',
      background: '#fff7e6',
      borderColor: '#ffd591',
      text: 'Pending Activation',
      icon: <MailOutlined />,
    },
    inactive: {
      color: '#8c8c8c',
      background: '#f5f5f5',
      borderColor: '#d9d9d9',
      text: 'Inactive',
      icon: <ClockCircleOutlined />,
    },
    suspended: {
      color: '#faad14',
      background: '#fff7e6',
      borderColor: '#ffd666',
      text: 'Suspended',
      icon: <PauseCircleOutlined />,
    },
    deleted: {
      color: '#ff4d4f',
      background: '#fff2f0',
      borderColor: '#ffadd2',
      text: 'Deleted',
      icon: <DeleteOutlined />,
    },
  },

  // 用户角色
  userRole: {
    admin: {
      color: '#722ed1',
      background: '#f9f0ff',
      borderColor: '#d3adf7',
      text: 'Administrator',
      icon: <CrownOutlined />,
    },
    exchange: {
      color: '#1890ff',
      background: '#f0f5ff',
      borderColor: '#adc6ff',
      text: 'Exchange',
      icon: <BankOutlined />,
    },
    issuer: {
      color: '#13c2c2',
      background: '#e6fffb',
      borderColor: '#87e8de',
      text: 'Issuer',
      icon: <BuildOutlined />,
    },
    intermediary: {
      color: '#fa8c16',
      background: '#fff7e6',
      borderColor: '#ffd591',
      text: 'Intermediary',
      icon: <ToolOutlined />,
    },
    operator: {
      color: '#eb2f96',
      background: '#fff0f6',
      borderColor: '#ffadd2',
      text: 'Operator',
      icon: <CrownOutlined />,
    },
    service_provider: {
      color: '#52c41a',
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'Service Provider',
      icon: <ToolOutlined />,
    },
  },

  // 渠道状态
  channelStatus: {
    active: {
      color: '#52c41a',
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'Active',
      icon: <CheckCircleOutlined />,
    },
    inactive: {
      color: '#8c8c8c',
      background: '#f5f5f5',
      borderColor: '#d9d9d9',
      text: 'Inactive',
      icon: <StopOutlined />,
    },
    maintenance: {
      color: '#faad14',
      background: '#fff7e6',
      borderColor: '#ffd666',
      text: 'Maintenance',
      icon: <ToolOutlined />,
    },
  },

  // 评估状态
  assessment: {
    draft: {
      color: '#8c8c8c',
      background: '#f5f5f5',
      borderColor: '#d9d9d9',
      text: 'Draft',
      icon: <EditOutlined />,
    },
    submitted: {
      color: '#1890ff',
      background: '#e6f7ff',
      borderColor: '#91d5ff',
      text: 'Submitted',
      icon: <SendOutlined />,
    },
    in_review: {
      color: '#faad14',
      background: '#fff7e6',
      borderColor: '#ffd666',
      text: 'In Review',
      icon: <ClockCircleOutlined />,
    },
    approved: {
      color: '#52c41a',
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'Approved',
      icon: <CheckCircleOutlined />,
    },
    rejected: {
      color: '#ff4d4f',
      background: '#fff2f0',
      borderColor: '#ffccc7',
      text: 'Rejected',
      icon: <CloseCircleOutlined />,
    },
  },

  // SPO流程状态
  spoStatus: {
    DRAFT: {
      color: '#8c8c8c',
      background: '#f5f5f5',
      borderColor: '#d9d9d9',
      text: 'Draft',
      icon: <EditOutlined />,
    },
    IN_PROGRESS: {
      color: '#1890ff',
      background: '#e6f7ff',
      borderColor: '#91d5ff',
      text: 'In Progress',
      icon: <PlayCircleOutlined />,
    },
    COMPLETED: {
      color: '#52c41a',
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'Completed',
      icon: <CheckCircleOutlined />,
    },
    CANCELLED: {
      color: '#ff4d4f',
      background: '#fff2f0',
      borderColor: '#ffccc7',
      text: 'Cancelled',
      icon: <StopOutlined />,
    },
  },

  // EL状态
  elStatus: {
    PENDING_GENERATION: {
      color: '#8c8c8c',
      background: '#f5f5f5',
      borderColor: '#d9d9d9',
      text: 'Pending Generation',
      icon: <ClockCircleOutlined />,
    },
    BRAVOWHALE_SIGNED: {
      color: '#1890ff',
      background: '#e6f7ff',
      borderColor: '#91d5ff',
      text: 'Bravowhale Signed',
      icon: <CheckCircleOutlined />,
    },
    ISSUER_SIGNED: {
      color: '#13c2c2',
      background: '#e6fffb',
      borderColor: '#87e8de',
      text: 'Issuer Signed',
      icon: <CheckCircleOutlined />,
    },
    SENT_TO_SP: {
      color: '#722ed1',
      background: '#f9f0ff',
      borderColor: '#d3adf7',
      text: 'Sent to S&P',
      icon: <SendOutlined />,
    },
    SIGNED_BY_SP: {
      color: '#52c41a',
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'S&P Signed',
      icon: <CheckCircleOutlined />,
    },
    PAYMENT_PENDING: {
      color: '#faad14',
      background: '#fff7e6',
      borderColor: '#ffd666',
      text: 'Payment Pending',
      icon: <DollarOutlined />,
    },
    PAYMENT_MADE: {
      color: '#52c41a',
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'Payment Completed',
      icon: <DollarOutlined />,
    },
  },

  // 付款状态
  paymentStatus: {
    PENDING: {
      color: '#8c8c8c',
      background: '#f5f5f5',
      borderColor: '#d9d9d9',
      text: 'Pending Upload',
      icon: <ClockCircleOutlined />,
    },
    UPLOADED: {
      color: '#1890ff',
      background: '#e6f7ff',
      borderColor: '#91d5ff',
      text: 'Pending Review',
      icon: <FileTextOutlined />,
    },
    VERIFIED: {
      color: '#52c41a',
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'Verified',
      icon: <CheckCircleOutlined />,
    },
    REJECTED: {
      color: '#ff4d4f',
      background: '#fff2f0',
      borderColor: '#ffccc7',
      text: 'Rejected',
      icon: <CloseCircleOutlined />,
    },
  },

  // SPO评估状态
  evaluationStatus: {
    NOT_STARTED: {
      color: '#8c8c8c',
      background: '#f5f5f5',
      borderColor: '#d9d9d9',
      text: 'Not Started',
      icon: <ClockCircleOutlined />,
    },
    ISSUER_SUBMITTED: {
      color: '#1890ff',
      background: '#e6f7ff',
      borderColor: '#91d5ff',
      text: 'Submitted',
      icon: <SendOutlined />,
    },
    FRAMEWORK_GENERATED: {
      color: '#13c2c2',
      background: '#e6fffb',
      borderColor: '#87e8de',
      text: 'Framework Generated',
      icon: <FileTextOutlined />,
    },
    IN_COMMUNICATION: {
      color: '#722ed1',
      background: '#f9f0ff',
      borderColor: '#d3adf7',
      text: 'In Communication',
      icon: <FormOutlined />,
    },
    COMPLETED: {
      color: '#52c41a',
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'Completed',
      icon: <CheckCircleOutlined />,
    },
  },

  // SPO结果状态
  resultStatus: {
    NOT_STARTED: {
      color: '#8c8c8c',
      background: '#f5f5f5',
      borderColor: '#d9d9d9',
      text: 'Not Started',
      icon: <ClockCircleOutlined />,
    },
    DRAFT_ISSUED: {
      color: '#1890ff',
      background: '#e6f7ff',
      borderColor: '#91d5ff',
      text: 'Draft Issued',
      icon: <FileTextOutlined />,
    },
    FEEDBACK_RECEIVED: {
      color: '#722ed1',
      background: '#f9f0ff',
      borderColor: '#d3adf7',
      text: 'Feedback Received',
      icon: <FormOutlined />,
    },
    FINAL_ISSUED: {
      color: '#52c41a',
      background: '#f6ffed',
      borderColor: '#b7eb8f',
      text: 'Final Report Issued',
      icon: <FileDoneOutlined />,
    },
  },
} as const;

const getStatusI18nKey = (type: StatusType, status: string): string =>
  `common:statusTag.${type}.${status}`;

// 状态类型
export type StatusType = keyof typeof UNIFIED_STATUS_CONFIG;
export type StatusValue<T extends StatusType> =
  keyof (typeof UNIFIED_STATUS_CONFIG)[T];

// 组件Props接口
export interface UnifiedStatusTagProps<T extends StatusType = StatusType>
  extends Omit<TagProps, 'color'> {
  /** 状态类型 */
  type: T;
  /** 状态值 */
  status: StatusValue<T>;
  /** 是否显示图标 */
  showIcon?: boolean;
  /** 自定义图标 */
  icon?: React.ReactNode;
  /** 尺寸 */
  size?: 'small' | 'middle' | 'large';
  /** 是否为实心样式 */
  filled?: boolean;
  /** 测试ID */
  testId?: string;
}

/**
 * 统一状态标签组件
 */
export const UnifiedStatusTag = <T extends StatusType>({
  type,
  status,
  showIcon = true,
  icon,
  size = 'middle',
  filled = false,
  testId = 'unified-status-tag',
  className = '',
  style = {},
  ...restProps
}: UnifiedStatusTagProps<T>) => {
  const { t } = useTranslation();

  // 获取状态配置
  const statusConfig = UNIFIED_STATUS_CONFIG[type]?.[
    status as keyof (typeof UNIFIED_STATUS_CONFIG)[T]
  ] as StatusConfig;

  if (!statusConfig) {
    console.warn(
      `UnifiedStatusTag: status config not found for ${type}.${String(status)}`
    );
    return (
      <Tag className={`unified-status-tag unknown ${className}`} {...restProps}>
        {String(status)}
      </Tag>
    );
  }

  // 计算样式
  const tagStyle: React.CSSProperties = {
    color: statusConfig.color,
    backgroundColor: filled ? statusConfig.color : statusConfig.background,
    borderColor: statusConfig.borderColor,
    border: `1px solid ${statusConfig.borderColor}`,
    borderRadius: '6px',
    padding:
      size === 'small' ? '2px 8px' : size === 'large' ? '6px 14px' : '4px 12px',
    fontSize: size === 'small' ? '12px' : size === 'large' ? '16px' : '14px',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: showIcon ? '4px' : '0',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    ...(filled && {
      color: '#ffffff',
    }),
    ...style,
  };

  // 渲染图标
  const renderIcon = () => {
    if (!showIcon) return null;

    const iconElement = icon || statusConfig.icon;
    if (!iconElement) return null;

    return React.cloneElement(iconElement as React.ReactElement, {
      style: {
        fontSize:
          size === 'small' ? '12px' : size === 'large' ? '16px' : '14px',
        color: filled ? '#ffffff' : statusConfig.color,
      },
    });
  };

  return (
    <Tag
      className={`unified-status-tag ${type} ${String(status)} ${size} ${filled ? 'filled' : 'outlined'} ${className}`}
      style={tagStyle}
      data-testid={testId}
      data-type={type}
      data-status={status}
      {...restProps}
    >
      {renderIcon()}
      <span>
        {t(getStatusI18nKey(type, String(status)), {
          // Fall back to the canonical English label when i18n keys are missing.
          defaultValue: statusConfig.text || String(status),
        })}
      </span>
    </Tag>
  );
};

// 便捷的状态标签组件
export const QuestionnaireStatusTag: React.FC<
  Omit<UnifiedStatusTagProps<'questionnaire'>, 'type'>
> = props => <UnifiedStatusTag type="questionnaire" {...props} />;

export const RiskLevelTag: React.FC<
  Omit<UnifiedStatusTagProps<'riskLevel'>, 'type'>
> = props => <UnifiedStatusTag type="riskLevel" {...props} />;

export const ProjectStatusTag: React.FC<
  Omit<UnifiedStatusTagProps<'project'>, 'type'>
> = props => <UnifiedStatusTag type="project" {...props} />;

export const UserStatusTag: React.FC<
  Omit<UnifiedStatusTagProps<'userStatus'>, 'type'>
> = props => <UnifiedStatusTag type="userStatus" {...props} />;

export const UserRoleTag: React.FC<
  Omit<UnifiedStatusTagProps<'userRole'>, 'type'>
> = props => <UnifiedStatusTag type="userRole" {...props} />;

export const ChannelStatusTag: React.FC<
  Omit<UnifiedStatusTagProps<'channelStatus'>, 'type'>
> = props => <UnifiedStatusTag type="channelStatus" {...props} />;

export const DecisionStatusTag: React.FC<
  Omit<UnifiedStatusTagProps<'decision'>, 'type'>
> = props => <UnifiedStatusTag type="decision" {...props} />;

export default UnifiedStatusTag;
